import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import urls, { stringifyParams } from '../util/urls';
import queryString from 'query-string';
import { ServiceAuth } from '../hooks/useService/useService';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';

export type Resource =
  | 'Patient'
  | 'Observation'
  | 'Person'
  | 'MedicationAdministration'
  | 'MedicationRequest'
  | 'MedicationStatement'
  | 'MedicationDispense'
  | 'DocumentReference'
  | 'Communication'
  | 'Condition'
  | 'Procedure'
  | 'Composition'
  | 'Encounter'
  | 'Flag'
  | 'Goal'
  | 'Provenance'
  | 'Practitioner'
  | 'Immunization'
  | 'AllergyIntolerance';

export type ResourceFhirType<TResource extends Resource> =
  | (TResource extends 'Patient' ? fhir.Patient : never)
  | (TResource extends 'Observation' ? fhir.Observation : never)
  | (TResource extends 'Person' ? fhir.Person : never)
  | (TResource extends 'MedicationRequest' ? fhir.MedicationRequest : never)
  | (TResource extends 'MedicationStatement' ? fhir.MedicationStatement : never)
  | (TResource extends 'MedicationDispense' ? fhir.MedicationDispense : never)
  | (TResource extends 'DocumentReference' ? fhir.DocumentReference : never)
  | (TResource extends 'Communication' ? fhir.Communication : never)
  | (TResource extends 'Condition' ? fhir.Condition : never)
  | (TResource extends 'MedicationAdministration'
      ? fhir.MedicationAdministration
      : never)
  | (TResource extends 'Procedure' ? fhir.Procedure : never)
  | (TResource extends 'Composition' ? fhir.Composition : never)
  | (TResource extends 'Encounter' ? fhir.Encounter : never)
  | (TResource extends 'Flag' ? fhir.Flag : never)
  | (TResource extends 'Goal' ? fhir.Goal : never)
  | (TResource extends 'Provenance' ? fhir.Provenance : never)
  | (TResource extends 'Practitioner' ? fhir.Practitioner : never)
  | (TResource extends 'Immunization' ? fhir.Resource : never);

export type CreatedResource<TResource extends Resource> = Omit<
  ResourceFhirType<TResource>,
  'id'
>;

export type LinkRelation = {
  relation: 'self' | 'next';
  url: string;
};

export type FhirServiceEntry<TResource extends Resource> = {
  resource: ResourceFhirType<TResource>;
  fullUrl: string;
};

export type FhirServiceResponse<TResource extends Resource> = {
  resourceType: 'Bundle';
  link: LinkRelation[];
  type: 'searchset';
  entry: FhirServiceEntry<TResource>[];
};

interface Options {
  axiosOptions?: AxiosRequestConfig;
  fhirVersion?: string;
  [key: string]: any;
}

export default class FhirService<TResource extends Resource> {
  getFhirAPI: (auth: ServiceAuth) => AxiosInstance;
  private _resource: TResource;
  private version: string;

  constructor(resource?: TResource, _options?: Partial<Options>) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._resource = resource;
    this.getFhirAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
    this.version = _options?.fhirVersion ? _options.fhirVersion : 'dstu3';
  }

  /**
   * Dynamic FHIR request not bound to a specific resource type
   */
  openGet =
    (auth: ServiceAuth) =>
    async (
      account: string,
      fhirPath: string,
      params: any = {},
    ): Promise<AxiosResponse<any>> => {
      const url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        fhirPath +
        '?' +
        queryString.stringify(params);
      return this.getFhirAPI(auth).get<any>(url);
    };

  uploadBundle =
    (auth: ServiceAuth) =>
    async (
      resources: fhir.DomainResource[],
      params: any = {},
    ): Promise<AxiosResponse<void>> => {
      const url = urls.api.fhir.bundleUpload(
        { account: auth.account, params },
        this.version,
      );
      return this.getFhirAPI(auth).post<void>(url, {
        type: 'collection',
        resourceType: 'Bundle',
        entry: (resources || []).map(resource => ({ resource })),
      });
    };

  /**
   * Dynamic FHIR request not bound to a specific resource type
   */
  openPut =
    (auth: ServiceAuth) =>
    async ({
      account,
      resourceType,
      id,
      updatedResource,
      cohortId,
      confirmNoConflict = false,
    }: {
      account: string;
      resourceType: Resource;
      id: string;
      updatedResource: fhir.Resource;
      cohortId?: string;
      confirmNoConflict?: boolean;
    }): Promise<AxiosResponse<any>> => {
      let url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        `${resourceType}/${id}`;
      if (cohortId) {
        url += `?cohort=${cohortId}`;
      }
      const headers: Record<string, string> = {};

      if (confirmNoConflict && updatedResource?.meta?.versionId) {
        headers['If-Match'] = updatedResource.meta.versionId;
      }

      return this.getFhirAPI(auth).put<any>(url, updatedResource, { headers });
    };

  getById =
    (auth: ServiceAuth) =>
    async (
      account: string,
      id: string,
    ): Promise<AxiosResponse<ResourceFhirType<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to fetch FHIR data. Please provide it in the constructor.',
        );
      }
      const url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        `${this._resource}/${id}`;
      return this.getFhirAPI(auth).get<ResourceFhirType<TResource>>(url);
    };

  getByIds =
    (auth: ServiceAuth) =>
    async (
      account: string,
      ids: string[],
    ): Promise<AxiosResponse<FhirServiceResponse<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to fetch FHIR data. Please provide it in the constructor.',
        );
      }
      const url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        `${this._resource}` +
        stringifyParams({ _id: ids }, { arrayFormat: 'comma' });
      return this.getFhirAPI(auth).get(url);
    };

  get =
    (auth: ServiceAuth) =>
    async (
      account: string,
      params: any = {},
    ): Promise<AxiosResponse<FhirServiceResponse<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to fetch FHIR data. Please provide it in the constructor.',
        );
      }
      const url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        `${this._resource}/` +
        stringifyParams(params);
      return this.getFhirAPI(auth).get<FhirServiceResponse<TResource>>(url);
    };

  search =
    (auth: ServiceAuth) =>
    async (
      account: string,
      project: string,
      params: any = {},
    ): Promise<AxiosResponse<FhirServiceResponse<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to fetch FHIR data. Please provide it in the constructor.',
        );
      }
      params._tag = [
        ...(params._tag || []),
        `http://lifeomic.com/fhir/dataset|${project}`,
      ];
      return this.getFhirAPI(auth).post(
        `${urls.api.fhir.baseFhirUrl(account, this.version)}${
          this._resource
        }/_search`,
        queryString.stringify(params),
      );
    };

  create =
    (auth: ServiceAuth) =>
    async (
      account: string,
      body: CreatedResource<TResource>,
      params: any = {},
    ): Promise<AxiosResponse<ResourceFhirType<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to fetch FHIR data. Please provide it in the constructor.',
        );
      }
      const url =
        urls.api.fhir.baseFhirUrl(account, this.version) +
        this._resource +
        stringifyParams(params);
      return this.getFhirAPI(auth).post<ResourceFhirType<TResource>>(url, body);
    };

  delete =
    (auth: ServiceAuth) =>
    async (
      account: string,
      resourceId: string,
      params: any = {},
    ): Promise<AxiosResponse<any>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to delete FHIR resource. Please provide it in the constructor.',
        );
      }
      const url = `${urls.api.fhir.baseFhirUrl(account, this.version)}${
        this._resource
      }/${resourceId}${stringifyParams(params)}`;
      return this.getFhirAPI(auth).delete<any>(url);
    };

  update =
    (auth: ServiceAuth) =>
    async (
      account: string,
      resource: ResourceFhirType<TResource>,
      params: any = {},
    ): Promise<AxiosResponse<FhirServiceResponse<TResource>>> => {
      if (!this._resource) {
        throw new Error(
          'Resource is required to update FHIR resource. Please provide it in the constructor.',
        );
      }

      const url = `${urls.api.fhir.baseFhirUrl(account, this.version)}${
        this._resource
      }/${resource.id}${stringifyParams(params)}`;
      return this.getFhirAPI(auth).put<FhirServiceResponse<TResource>>(
        url,
        resource,
      );
    };
}
